import React, { useEffect, useContext, useState } from "react";
import Head from "next/head";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  SlideIn,
  Text,
  Image,
  Flex,
  Box,
  IconButton,
} from "@chakra-ui/core";
import FoodOptions from "components/food/FoodOptions";
import FoodComments from "components/food/FoodComments";
import useWindowSize from "hooks/useWindowSize";
import { ContextStore } from "context/ContextStore";
import wait from "utils/shared/wait";
import { formatPrice, getPrice } from "utils/shared/prices";
import setItemsUnchecked from "utils/shared/setItemsUnchecked";
import LoadingFoodOptions from "components/food/LoadingFoodOptions";

const FoodModal = ({ optionsData, optionItemsData }) => {
  const {
    openFood,
    setOpenFood,
    currentOrder,
    setCurrentOrder,
    basketOrders,
    setBasketOrders,
    setBasketOpen,
    isModalOpen,
    setModalOpen,
    ouvert,
    isStoreManuallyClosed,
    isDashboardOnline,
    showToast,
  } = useContext(ContextStore);
  const [loadingButton, setLoadingButton] = useState(false);
  const [cantOrderYet, setCantOrderYet] = useState(false);
  const [hasMatchedOptions, setHasMatchedOptions] = useState(false);
  const [openFoodOptions, setOpenFoodOptions] = useState();

  const windowSize = useWindowSize();

  const isEditing = openFood && openFood.index > -1;

  useEffect(() => {
    if (openFood && optionsData && optionItemsData) {
      if (openFood?.options?.length) {
        const openFoodCopy = JSON.parse(JSON.stringify({ ...openFood })); // JSON thingy to make a deep copy
        let openFoodOptionsCopy = JSON.parse(JSON.stringify([...openFoodCopy.options]));

        for (let i = openFoodOptionsCopy.length - 1; i >= 0; i--) {
          // backward loop because splice alters the length of array
          // https://stackoverflow.com/questions/9882284/looping-through-array-and-removing-items-without-breaking-for-loop
          const matchingOptionData = optionsData.find((item) => item.id === openFoodOptionsCopy[i].optionId);

          if (matchingOptionData) {
            openFoodOptionsCopy[i] = {
              ...openFoodOptionsCopy[i],
              name: matchingOptionData.name,
              description: matchingOptionData.description,
              min: matchingOptionData.min,
              max: matchingOptionData.max,
              optionItems: matchingOptionData.optionItems,
              optionId: matchingOptionData.id,
              // Ticket design specific props below
              ...(matchingOptionData.ticketTitlePrefix && { ticketTitlePrefix: matchingOptionData.ticketTitlePrefix }),
              ...(matchingOptionData.ticketTitleSuffix && { ticketTitleSuffix: matchingOptionData.ticketTitleSuffix }),
            };
          } else {
            openFoodOptionsCopy.splice(i, 1); // removes an undefined (deleted) option from the food.
          }

          for (let ii = openFoodOptionsCopy[i].optionItems.length - 1; ii >= 0; ii--) {
            const matchingOptionItemData = optionItemsData.find(
              (item) => item.id === openFoodOptionsCopy[i].optionItems[ii].optionItemId
            );
            if (matchingOptionItemData) {
              openFoodOptionsCopy[i].optionItems[ii] = {
                ...openFoodOptionsCopy[i].optionItems[ii],
                name: matchingOptionItemData.name,
                ...(matchingOptionItemData.description && { description: matchingOptionItemData.description }),
                isAvailable: matchingOptionItemData.isAvailable,
                // Ticket design specific props below
                ...(matchingOptionItemData.isVisibleOnTicket !== undefined && {
                  isVisibleOnTicket: matchingOptionItemData.isVisibleOnTicket,
                }),
                ...(matchingOptionItemData.ticketText && { ticketText: matchingOptionItemData.ticketText }),
                ...(matchingOptionItemData.ticketReplaceNameTarget && {
                  ticketReplaceNameTarget: matchingOptionItemData.ticketReplaceNameTarget,
                }),
                ...(matchingOptionItemData.ticketReplaceNameValue && {
                  ticketReplaceNameValue: matchingOptionItemData.ticketReplaceNameValue,
                }),
              };
            } else {
              openFoodOptionsCopy[i].optionItems.splice(ii, 1); // removes a delete choice from the option
            }
          }

          if (!openFoodOptionsCopy[i].optionItems.length) {
            openFoodOptionsCopy.splice(i, 1); // removes options badly set by admin without choices
          }
        }
        setOpenFoodOptions(openFoodOptionsCopy);
        wait(500).then(() => setHasMatchedOptions(true));
      } else {
        setHasMatchedOptions(true);
      }
    }
  }, [openFood]);

  // Sets the order from the openFood
  useEffect(() => {
    if (isEditing && openFood) {
      setCurrentOrder({ ...openFood });
    } else if (!isEditing && openFood && hasMatchedOptions && openFoodOptions) {
      let openFoodCopy = { ...openFood, options: openFoodOptions };
      for (let i = 0; i < openFoodCopy.options.length; i++) {
        setItemsUnchecked(openFoodCopy.options[i].optionItems);
      }
      setCurrentOrder({ ...openFoodCopy, quantity: 1, comments: "" });
    } else if (!isEditing && openFood && hasMatchedOptions && !openFood?.options?.length) {
      setCurrentOrder({ ...openFood, quantity: 1, comments: "" });
    }
  }, [hasMatchedOptions, openFoodOptions, openFood]);

  // Checks if all min options are selected to let the user order
  useEffect(() => {
    if (currentOrder && currentOrder.options) {
      for (let n = 0; n < currentOrder.options.length; n++) {
        if (currentOrder.options[n].optionItems.filter((item) => item.checked).length < currentOrder.options[n].min) {
          return setCantOrderYet(true);
        } else if (n + 1 === currentOrder.options.length) {
          return setCantOrderYet(false);
        } else {
          // Nothing here?
        }
      }
    }
  }, [currentOrder]);

  // Edit the order when food comes from the basket
  const editOrder = () => {
    const newBasketOrders = [...basketOrders];
    newBasketOrders[openFood.index] = currentOrder;
    setBasketOrders(newBasketOrders);
  };

  // Handles the order quantity
  const handleQuantity = (amount) => {
    if (currentOrder.quantity >= 1 && amount > 0 && currentOrder.quantity < 99) {
      setCurrentOrder({ ...currentOrder, quantity: currentOrder.quantity + amount });
    } else if (currentOrder.quantity > 1 && amount < 0) {
      setCurrentOrder({ ...currentOrder, quantity: currentOrder.quantity + amount });
    }
  };

  // Adds the current order to the basket
  const handleAdd = () => {
    setLoadingButton(true);
    isEditing
      ? editOrder(currentOrder)
      : setBasketOrders([...basketOrders, JSON.parse(JSON.stringify({ ...currentOrder }))]);
    showToast({
      title: "Produit ajouté au panier !",
    });
    handleClose();
  };

  const alertUser = () => {
    showToast({
      title: "Commande incomplète",
      body: "Vérifiez votre commande, des options obligatoires n'ont pas été sélectionnées",
      type: "warning",
    });
  };

  // Closes the modal and resets data
  const handleClose = () => {
    setModalOpen(false);
    wait(100).then(() => {
      setLoadingButton(false);
      setOpenFood(undefined);
      setCurrentOrder(undefined);
      setOpenFoodOptions(undefined);
      setCantOrderYet(false);
      setHasMatchedOptions(false);
      isEditing && windowSize.width < 768 && setBasketOpen(true);
    });
  };

  // Dynamically set the mobile height (100vh does not work well with the address bar)
  const mobileHeight = windowSize.height + "px";

  return (
    <>
      {openFood ? (
        <Head>
          <title>{openFood.name} | La Citadelle</title>
        </Head>
      ) : null}

      <SlideIn in={isModalOpen}>
        {(styles) => (
          <Modal
            onClose={handleClose}
            closeOnOverlayClick={false}
            isOpen={true}
            size={{ base: "full", sm: "xl" }}
            isCentered
          >
            <ModalOverlay opacity={styles.opacity} />
            <ModalContent borderRadius="lg" {...styles} my={{ base: "0", sm: "2rem" }}>
              <ModalHeader textAlign="center" borderBottom="1px" borderColor="gray.100">
                {openFood?.name}
              </ModalHeader>
              <ModalCloseButton mt="2" />
              <ModalBody
                py="6"
                px="0"
                overflowY="auto"
                maxHeight={{
                  base: `calc(${mobileHeight} - 8.5rem)`,
                  sm: "calc(100vh - 12.5rem)",
                }}
              >
                <Flex align="flex-start" justify="center" px="6" pb={5}>
                  {openFood?.image ? (
                    <Box flexShrink={0}>
                      <Image
                        src={openFood.image}
                        rounded="md"
                        fallbackSrc="/images/fallback-image.jpg"
                        h={{ base: 24, sm: 32, md: 40 }}
                        w={{ base: 24, sm: 32, md: 40 }}
                        mr={5}
                        objectFit="cover"
                      />
                    </Box>
                  ) : null}
                  <Box w="full">
                    <Text fontSize="md" color="gray.900" fontWeight="bold">
                      {openFood?.name}
                    </Text>
                    <Text fontSize="sm" color="gray.700" fontWeight="normal">
                      {openFood?.description}
                    </Text>
                  </Box>
                </Flex>

                {currentOrder?.options?.length > 0 && hasMatchedOptions && <FoodOptions />}
                {openFood?.options?.length > 0 && !hasMatchedOptions && <LoadingFoodOptions />}
                {ouvert === true && openFood?.isAvailable && <FoodComments />}
              </ModalBody>
              <ModalFooter
                py="4"
                borderBottomLeftRadius="lg"
                borderBottomRightRadius="lg"
                bg="gray.50"
                borderTop="1px"
                borderColor="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <IconButton
                  variantColor="brand"
                  variant="outline"
                  size="xs"
                  aria-label="Moins"
                  icon="minus"
                  rounded="full"
                  border="2px"
                  onClick={() => handleQuantity(-1)}
                  isDisabled={!ouvert || !openFood?.isAvailable}
                />
                <Flex
                  h="10"
                  w="10"
                  minW="10"
                  minH="10"
                  fontSize="lg"
                  fontWeight="medium"
                  color="gray.700"
                  align="center"
                  justify="center"
                >
                  {currentOrder?.quantity}
                </Flex>
                <IconButton
                  variantColor="brand"
                  variant="ghost"
                  size="xs"
                  aria-label="Plus"
                  icon="add"
                  rounded="full"
                  border="2px"
                  mr="5"
                  onClick={() => handleQuantity(+1)}
                  isDisabled={!ouvert || !openFood?.isAvailable}
                />
                <Button
                  variantColor={cantOrderYet ? "gray" : "brand"}
                  variant={cantOrderYet ? "outline" : "solid"}
                  w="full"
                  rounded="lg"
                  shadow="md"
                  onClick={cantOrderYet ? alertUser : handleAdd}
                  isLoading={loadingButton}
                  loadingText="Ajout en cours..."
                  isDisabled={!ouvert || !openFood?.isAvailable || !currentOrder || !isDashboardOnline}
                >
                  {openFood?.isAvailable &&
                    isEditing &&
                    currentOrder &&
                    ouvert &&
                    isDashboardOnline &&
                    `Modifier ${currentOrder?.quantity} (${formatPrice(getPrice(currentOrder))})`}
                  {openFood?.isAvailable &&
                    isEditing &&
                    currentOrder &&
                    ouvert &&
                    !isDashboardOnline &&
                    `(${formatPrice(getPrice(currentOrder))}) - Restaurant Indisponible`}
                  {openFood?.isAvailable &&
                    !isEditing &&
                    currentOrder &&
                    ouvert &&
                    isDashboardOnline &&
                    `Ajouter ${currentOrder?.quantity} (${formatPrice(getPrice(currentOrder))})`}
                  {openFood?.isAvailable &&
                    !isEditing &&
                    currentOrder &&
                    ouvert &&
                    !isDashboardOnline &&
                    `(${formatPrice(getPrice(currentOrder))}) - Restaurant Indisponible`}
                  {!openFood?.isAvailable &&
                    ouvert &&
                    isDashboardOnline &&
                    currentOrder &&
                    `${formatPrice(getPrice(currentOrder))} - Indisponible`}
                  {!ouvert &&
                    !isStoreManuallyClosed &&
                    currentOrder &&
                    `${formatPrice(getPrice(currentOrder))} - Restaurant fermé`}
                  {!ouvert &&
                    isStoreManuallyClosed &&
                    currentOrder &&
                    `${formatPrice(getPrice(currentOrder))} - Fermé temporairement`}
                  {openFood?.isAvailable && !currentOrder && "Chargement..."}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </SlideIn>
    </>
  );
};
export default FoodModal;
